/**
 * Mentor Coach Qualification (MCQ) training drill types.
 *
 * Mirror of the FastAPI schemas in
 * backend/app/api/endpoints/mentor_training.py.
 */

export const COMPETENCY_CODES = [
  'C1',
  'C2',
  'C3',
  'C4',
  'C5',
  'C6',
  'C7',
  'C8',
] as const;

export type CompetencyCode = typeof COMPETENCY_CODES[number];

export const VALID_LEVELS = [
  'below_acc',
  'acc',
  'ready_for_pcc',
  'pcc',
  'mcc',
] as const;

export type DrillLevel = typeof VALID_LEVELS[number];

export const TARGET_LEVELS = ['acc', 'pcc', 'mcc'] as const;
export type DrillTargetLevel = typeof TARGET_LEVELS[number];

export type DrillAttemptStatus = 'not_started' | 'in_progress' | 'submitted';

export interface DrillCreateRequest {
  cohort_id: number;
  source_document_id: number;
  title: string;
  target_level: DrillTargetLevel;
  module_name?: string | null;
  instructions?: string | null;
  publish_now?: boolean;
}

export interface DrillSummary {
  id: number;
  cohort_id: number;
  school_id: number;
  title: string;
  module_name: string | null;
  target_level: DrillTargetLevel;
  is_published: boolean;
  published_at: string | null;
  archived_at: string | null;
  created_at: string;
  attempt_count: number;
  submitted_count: number;
  my_attempt_status: DrillAttemptStatus | null;
}

export interface DrillDetail {
  id: number;
  cohort_id: number;
  school_id: number;
  title: string;
  module_name: string | null;
  target_level: DrillTargetLevel;
  instructions: string | null;
  transcript_excerpt: string | null;
  has_transcript: boolean;
  is_published: boolean;
  published_at: string | null;
  archived_at: string | null;
  created_at: string;
  competency_codes: CompetencyCode[];
}

export interface StudentCompetencyGrading {
  level: DrillLevel;
  evidence_quote: string;
}

export interface StudentWrittenFeedback {
  strength: string;
  growth: string;
}

export interface AttemptSubmitRequest {
  competencies: Record<CompetencyCode, StudentCompetencyGrading>;
  overall_level: DrillLevel;
  written_feedback: StudentWrittenFeedback;
  language?: string;
}

export interface AgreementPerCompetency {
  code: CompetencyCode;
  student_level: DrillLevel | null;
  ground_truth_level: DrillLevel | null;
  delta_steps: number | null;
  score: number;
  evidence_quote: string;
  ground_truth_evidence: string[];
}

export interface AgreementResult {
  per_competency: AgreementPerCompetency[];
  overall_student_level: DrillLevel | null;
  overall_ground_truth_level: DrillLevel | null;
  overall_delta_steps: number | null;
  overall_score: number;
  score_percent: number;
  exact_match_count: number;
  total_competencies: number;
}

export interface FeedbackCritiquePart {
  score: number;
  review: string;
  suggested_rewrite?: string;
}

export interface FeedbackCritique {
  strengths_quality: FeedbackCritiquePart;
  growth_quality: FeedbackCritiquePart;
  alignment_score: number;
  tips: string[];
  _fallback?: boolean;
}

export interface DrillGroundTruthSummary {
  overall_level: DrillLevel;
  competencies: Partial<Record<CompetencyCode, { level: DrillLevel; evidence_quotes: string[] }>>;
}

export interface AttemptResponse {
  id: number;
  drill_id: number;
  student_user_id: number;
  status: DrillAttemptStatus;
  started_at: string | null;
  submitted_at: string | null;
  student_grading: AttemptSubmitRequest | null;
  agreement_result: AgreementResult | null;
  feedback_critique: FeedbackCritique | null;
  ground_truth_summary: DrillGroundTruthSummary | null;
}

export interface InstructorAttemptRow {
  id: number;
  drill_id: number;
  student_user_id: number;
  student_name: string | null;
  student_email: string | null;
  status: DrillAttemptStatus;
  submitted_at: string | null;
  score_percent: number | null;
  alignment_score: number | null;
}

export const COMPETENCY_LABELS: Record<CompetencyCode, { en: string; tr: string }> = {
  C1: { en: 'Demonstrates Ethical Practice', tr: 'Etik Uygulamayı Sergiler' },
  C2: { en: 'Embodies the Coaching Mindset', tr: 'Koçluk Zihniyetini Yaşar' },
  C3: { en: 'Establishes & Maintains Agreements', tr: 'Anlaşmalar Kurar ve Sürdürür' },
  C4: { en: 'Cultivates Trust & Safety', tr: 'Güven ve Güvenliği Besler' },
  C5: { en: 'Maintains Presence', tr: 'Var Oluşunu Korur' },
  C6: { en: 'Listens Actively', tr: 'Etkin Dinler' },
  C7: { en: 'Evokes Awareness', tr: 'Farkındalığı Uyandırır' },
  C8: { en: 'Facilitates Client Growth', tr: 'Müşteri Gelişimini Kolaylaştırır' },
};

export const LEVEL_LABELS: Record<DrillLevel, { en: string; tr: string }> = {
  below_acc: { en: 'Below ACC', tr: 'ACC Altı' },
  acc: { en: 'ACC level', tr: 'ACC Düzeyi' },
  ready_for_pcc: { en: 'PCC preparation band', tr: 'PCC Hazırlık Bandı' },
  pcc: { en: 'PCC level', tr: 'PCC Düzeyi' },
  mcc: { en: 'MCC level', tr: 'MCC Düzeyi' },
};

export const LEVEL_MODIFIER_LABELS = {
  mcc_indicators_emerging: {
    en: 'MCC-level indicators emerging',
    tr: 'MCC düzeyi göstergeler belirgin',
  },
  pcc_upper_mcc_adjacent: {
    en: 'Upper PCC / approaching MCC profile',
    tr: 'PCC üstü / MCC’ye yaklaşan profil',
  },
} as const;
